/* istanbul ignore file */

import {Alert, AlertDescription, AlertIcon, AlertTitle, Box, Code, Stack} from '@chakra-ui/react'
import React, {memo, useCallback, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useCommerceAPI} from '../../commerce-api/contexts'
import LoadingSpinner from '../loading-spinner'
import useCmsLazyImgLoad from './hooks/use-cms-lazy-img-load'
import useCmsScripts from './hooks/use-cms-scripts'
import {useWidgets} from '../../widgets'
import {Helmet} from 'react-helmet'
import tns from 'tiny-slider/dist/tiny-slider.css'

const CMSContent = ({id, content: preloadedContent, nonce, showError = true, ...htmlProps}) => {
    const api = useCommerceAPI()

    const [root, setRoot] = useState(null)
    const [loading, setLoading] = useState(!preloadedContent)
    const [content, setContent] = useState(preloadedContent)
    const [error, setError] = useState(null)
    const mounted = useRef(false)
    const html = !loading && !error && content?.c_body

    const load = useCallback(
        async (cid) => {
            setLoading(true)
            try {
                setError(null)
                const content = await api.shopperContent.getContent({
                    parameters: {cid, html: true}
                })
                if (mounted.current) {
                    setContent(content)
                }
            } catch (e) {
                if (mounted.current) {
                    if (process.env.NODE_ENV !== 'production') {
                        console.error(`Error loading cms "${cid}"`, e)
                    }
                    setError(e)
                }
            } finally {
                if (mounted.current) {
                    setLoading(false)
                }
            }
        },
        [api]
    )

    useEffect(() => {
        mounted.current = true
        return () => {
            mounted.current = false
        }
    }, [])

    // load the cms module
    useEffect(() => {
        if (preloadedContent) {
            setContent(preloadedContent)
            setLoading(false)
            setError(null)
        } else {
            load(id)
        }
    }, [id, load, preloadedContent])

    // need to manually execute the embedded script tags
    useCmsScripts({
        html,
        root,
        onError: setError,
        displayName: id,
        nonce
    })

    useCmsLazyImgLoad(root, html)

    const widgets = useWidgets(root, html)

    // loading state
    if (loading) {
        return (
            <Stack>
                <LoadingSpinner wrapperStyles={{position: 'relative', height: '4em'}} />
            </Stack>
        )
    }

    // error state
    if (error) {
        if (showError) {
            return (
                <Alert {...htmlProps} status="error">
                    <AlertIcon />
                    <Box>
                        <AlertTitle>
                            Something bad happened while loading <Code>{id}</Code>!
                        </AlertTitle>
                        <AlertDescription>{error.toString()}</AlertDescription>
                    </Box>
                </Alert>
            )
        } else {
            console.error(`Error wile loading ${id}. ${error.toString()}`)
            return null
        }
    }

    const tnsStyle = typeof tns === 'string' ? tns : ''

    // render the module's html
    return (
        <>
            <Helmet>
                <style>{tnsStyle}</style>
            </Helmet>
            <Box
                className="cms-page-module"
                {...htmlProps}
                ref={setRoot}
                dangerouslySetInnerHTML={{__html: html}}
            />
            {widgets}
        </>
    )
}

// prevent unnecessary re-renders that will override the modified DOM from inline scripts
const MemoizedCMSContent = memo(CMSContent, (prevProps, nextProps) => {
    if (prevProps.id !== nextProps.id) {
        return false
    }
    // we only care about the c_body property that contains the raw html
    if (prevProps.content?.c_body !== nextProps.content?.c_body) {
        return false
    }
    // the nonce and showError are not expected to change during the lifetime of a component so no need to check them
    return true
})

if (process.env.NODE_ENV !== 'production') {
    MemoizedCMSContent.displayName = 'MemoizedCMSContent'
}

MemoizedCMSContent.propTypes = CMSContent.propTypes = {
    id: PropTypes.string.isRequired,
    content: PropTypes.object,
    nonce: PropTypes.string,
    showError: PropTypes.bool
}

export default MemoizedCMSContent
